import React, { useMemo, useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import clsx from "clsx";

import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  rectIntersection,
  MeasuringStrategy,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { motion } from "framer-motion";

import StepCard from "./components/StepCard";
import { useReorderStepsMutation } from "./queries/useJourneyQuery";
import styles from "./styles.module.scss";
import StepProperties from "./components/StepProperties";
import useGroupedSteps from "./hooks/useGroupedSteps";
// import CreateGroup from "./components/CreateGroup";
// import GroupProperties from "./components/GroupProperties";
import EmptyJourney from "../EmptyJourney";
import StickySideBar from "./components/StickySideBar";
import { shouldShowTestGenerationDebug } from "utils/addExternalScripts";
import {
  formatTokenCount,
  formatUsdCost,
  getDiscoveryDebugSummary,
} from "utils/discoveryDebug";

const PAGE_BLOCKS_ROUTES = [
  "v1/page-blocks/",
  "v1/exploratory/discover-page-blocks",
];

const getNumericValue = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const buildRouteScopedDebug = (debugInfo, routes) => {
  if (!debugInfo) return null;

  const routeList = Array.isArray(routes) ? routes : [routes];

  const llmLogs = Array.isArray(debugInfo?.llm_logs)
    ? debugInfo.llm_logs.filter((row) => routeList.includes(row?.route))
    : [];

  if (!llmLogs.length) {
    return null;
  }

  const totals = llmLogs.reduce(
    (acc, row) => {
      acc.total_usd += getNumericValue(row?.llm_cost_usd);
      acc.input_tokens += getNumericValue(row?.llm_usage?.input_tokens);
      acc.output_tokens += getNumericValue(row?.llm_usage?.output_tokens);
      acc.priced_calls += getNumericValue(row?.llm_usage?.priced_calls);
      return acc;
    },
    {
      total_usd: 0,
      input_tokens: 0,
      output_tokens: 0,
      priced_calls: 0,
    }
  );

  return {
    ...debugInfo,
    llm_logs: llmLogs,
    llm_usage_summary: {
      input_tokens: totals.input_tokens,
      output_tokens: totals.output_tokens,
      priced_calls: totals.priced_calls,
    },
    llm_cost_summary: {
      ...(debugInfo?.llm_cost_summary || {}),
      total_usd: Number(totals.total_usd.toFixed(8)),
      input_tokens: totals.input_tokens,
      output_tokens: totals.output_tokens,
      priced_calls: totals.priced_calls,
    },
  };
};

const SortableStepCard = ({
  step,
  selectedId,
  setSelectedId,
  isLast,
  // checkedForGroup,
  // handleCheck,
  // showGroupCheckbox
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: step.id });

  const isSelected = selectedId === step.id;

  const handleClick = (e) => {
    e.stopPropagation();
    setSelectedId(step.id);
  };

  return (
    <motion.div
      ref={setNodeRef}
      {...attributes}
      {...(isSelected ? listeners : {})}
      onClick={handleClick}
      layout={!isDragging}
      transition={{ type: "spring", stiffness: 500, damping: 30 }}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 999 : 1,
        pointerEvents: isDragging ? "none" : "auto",
      }}
    >
      <StepCard
        step={step}
        isSelected={isSelected}
        isDragging={isDragging}
        isLast={isLast}
        // checkedForGroup={checkedForGroup}
        // handleCheck={handleCheck}
        //showGroupCheckbox={showGroupCheckbox}
      />
    </motion.div>
  );
};

const TestJourneyTab = ({
  journeyId,
  steps: initialSteps,
  pageBlocksDiscoveryDebug,
  handleInfoBar,
}) => {
  const steps = Array.isArray(initialSteps) ? initialSteps : [];
  const hasSteps = steps.length > 0;
  const [showDebug, setShowDebug] = useState(false);
  const {
    grouped,
    // checkedForGroup,
    // setCheckedForGroup,
    setGrouped,
  } = useGroupedSteps({ steps });

  const [selectedId, setSelectedId] = useState(null);

  // const handleCheck = (id) => {
  //     setCheckedForGroup(state => state.includes(id) ? state.filter(item => item !== id) : [...state, id]);
  //     setSelectedId(null);
  // }

  const selectedStep = grouped.filter((item) => item.id === selectedId)[0];
  const routeScopedDebug = useMemo(
    () => buildRouteScopedDebug(pageBlocksDiscoveryDebug, PAGE_BLOCKS_ROUTES),
    [pageBlocksDiscoveryDebug]
  );

  useEffect(() => {
    if (!grouped.length) return;
    console.log("[Journey TestJourneyTab] steps:", grouped);
  }, [grouped]);

  useEffect(() => {
    if (!selectedStep) return;
    console.log("[Journey TestJourneyTab] step:", selectedStep);
  }, [selectedStep]);

  const reorderMutation = useReorderStepsMutation({
    journeyId,
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const itemIds = useMemo(() => grouped.map((s) => s.id), [grouped]);

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const oldIndex = grouped.findIndex((s) => s.id === active.id);
    const newIndex = grouped.findIndex((s) => s.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    if (oldIndex !== newIndex) {
      const moved = arrayMove(grouped, oldIndex, newIndex);
      const newGroups = [];
      let index = 1;

      moved.forEach((item) => {
        if (!item.steps || item.steps.length === 0) {
          newGroups.push({ ...item, order: index });
          index += 1;
          return;
        }

        const tempGroup = { ...item };
        const groupOrder = index;
        let stepOrder = groupOrder;

        const newStepsInGroup = tempGroup.steps.map((step) => {
          const s = { ...step, order: stepOrder };
          stepOrder += 1;
          return s;
        });

        tempGroup.order = groupOrder;
        tempGroup.steps = newStepsInGroup;
        newGroups.push(tempGroup);

        index = stepOrder;
      });

      const stepsToMutate = newGroups.flatMap((item) =>
        item.steps?.length
          ? item.steps.map((step) => ({
              id: step.id,
              order: step.order,
            }))
          : [
              {
                id: item.id,
                order: item.order,
              },
            ],
      );

      setGrouped(newGroups);
      await reorderMutation.mutateAsync(stepsToMutate);
    }
  };

  const handleDeselect = () => setSelectedId(null);

  const handleStepSelect = (id) => {
    setSelectedId((prev) => (prev === id ? null : id));
    // setCheckedForGroup([]);
  };

  // const isAllowedToGroup = (index) => {
  //     if (checkedForGroup.length === 0) return true;
  //     const allowed = Math.abs(index - grouped.findIndex(item => item.id === checkedForGroup[0])) <= 1;
  //     return allowed;
  // }

  // const handleGroupCleanUp = () => {
  //     setCheckedForGroup([]);
  // }

  // const isInfoBar = !!(selectedId || checkedForGroup?.length);
  const isInfoBar = !!selectedId;

  useEffect(() => {
    handleInfoBar(isInfoBar);
  }, [isInfoBar]);

  useEffect(() => {
    (async () => {
      try {
        const allowed = await shouldShowTestGenerationDebug();
        setShowDebug(allowed);
      } catch {
        setShowDebug(false);
      }
    })();
  }, []);

  const renderGenerationDebug = () => {
    if (!routeScopedDebug) return null;

    const { video_url, aws_tracing_url, llm_logs = [] } = routeScopedDebug;
    const debugSummary = getDiscoveryDebugSummary(routeScopedDebug);
    const primaryRoute = llm_logs[0]?.route || PAGE_BLOCKS_ROUTES[0];

    return (
      <details className={styles.debugDetails}>
        <summary className={styles.debugSummary}>
          Test generation debug
        </summary>
        <Box className={styles.debugContent}>
          <Typography className={styles.debugRoute}>
            {primaryRoute}
          </Typography>
          {debugSummary ? (
            <Box className={styles.debugStats}>
              <div>Total: {formatUsdCost(debugSummary.totalUsd)}</div>
              <div>
                Tokens: {formatTokenCount(debugSummary.inputTokens)} in /{" "}
                {formatTokenCount(debugSummary.outputTokens)} out
              </div>
              <div>
                Priced calls: {formatTokenCount(debugSummary.pricedCalls)}
              </div>
            </Box>
          ) : null}
          {video_url ? (
            <Box className={styles.debugMedia}>
              <div>Discovery video:</div>
              <video src={video_url} controls className={styles.debugVideo} />
            </Box>
          ) : null}
          {aws_tracing_url ? (
            <Box className={styles.debugMedia}>
              <a href={aws_tracing_url} target="_blank" rel="noreferrer">
                Open trace report
              </a>
            </Box>
          ) : null}
          {llm_logs.length ? (
            <Box className={styles.debugTableWrapper}>
              <table className={styles.debugTable}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>route</th>
                    <th>cost (usd)</th>
                    <th>request json</th>
                    <th>request ts</th>
                    <th>response json</th>
                    <th>response ts</th>
                    <th>page link</th>
                    <th>image</th>
                  </tr>
                </thead>
                <tbody>
                  {llm_logs.map((row, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td>{row?.route || "-"}</td>
                      <td>{formatUsdCost(row?.llm_cost_usd)}</td>
                      <td>
                        {row?.request_json ? (
                          <a
                            href={row.request_json}
                            target="_blank"
                            rel="noreferrer"
                          >
                            request_json
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>{row?.request_timestamp || "-"}</td>
                      <td>
                        {row?.response_json ? (
                          <a
                            href={row.response_json}
                            target="_blank"
                            rel="noreferrer"
                          >
                            response_json
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>{row?.response_timestamp || "-"}</td>
                      <td>
                        {row?.page_link ? (
                          <a
                            href={row.page_link}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {row.page_link}
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>
                        {row?.image ? (
                          <a href={row.image} target="_blank" rel="noreferrer">
                            screenshot
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          ) : null}
        </Box>
      </details>
    );
  };

  if (!hasSteps) {
    return (
      <Box className={clsx(styles.container, styles.emptyState)}>
        {showDebug ? renderGenerationDebug() : null}
        <EmptyJourney />
      </Box>
    );
  }

  return (
    <Box className={styles.container} onClick={handleDeselect}>
      {showDebug ? renderGenerationDebug() : null}
      <Box className={styles.contentRow}>
        <DndContext
          sensors={sensors}
          collisionDetection={rectIntersection}
          onDragEnd={handleDragEnd}
          measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
        >
          <Box
            className={styles.dndContent}
            sx={{
              maxWidth: isInfoBar ? "65%" : "100%",
            }}
          >
            <SortableContext items={itemIds} strategy={rectSortingStrategy}>
              <Box className={styles.list}>
                {grouped.map((step, index) => (
                  <SortableStepCard
                    key={step.id}
                    step={step}
                    selectedId={selectedId}
                    setSelectedId={handleStepSelect}
                    isLast={index === grouped.length - 1}
                    // checkedForGroup={checkedForGroup.includes(step.id)}
                    // handleCheck={handleCheck}
                    // showGroupCheckbox={isAllowedToGroup(index)}
                  />
                ))}
              </Box>
            </SortableContext>
          </Box>
        </DndContext>
        {selectedStep ? (
          <StickySideBar
            // element={checkedForGroup || selectedStep}
            element={selectedStep}
          >
            {/* <CreateGroup
                        checkedForGroup={checkedForGroup}
                        journeyId={journeyId}
                        onSuccess={handleGroupCleanUp}
                        onError={handleGroupCleanUp}
                    /> */}
            <StepProperties
              journeyId={journeyId}
              step={selectedStep}
              onSuccess={handleDeselect}
            />
            {/* <GroupProperties
                        journeyId={journeyId}
                        group={selectedStep}
                        onSuccess={handleDeselect}
                    /> */}
          </StickySideBar>
        ) : null}
      </Box>
    </Box>
  );
};

export default TestJourneyTab;
